import { ObservableMap, action, computed, makeObservable, observable, reaction } from "mobx";
import { SyntheticEvent, createContext, useContext } from "react";
import Spec from "csi/heading/Spec";
import { BLANK_COMPONENTITEM, ComponentItem } from "designSystem/types/component-item-types";
import { organizeComponentItems } from "csi/middleware/common";
import { ILocaleProvider } from "designSystem/locale";
import { functionsRequiringSecondKey, reportFunctions } from "../_query/ReportFunctions";
import { ICustomViewReport } from "src/c8-platform/type/customView";
import { getSpecForExtendedKeys } from "csi/middleware/customView/customViewDataManagementService";
import CustomViewAttributesStore from "csi/middleware/customView/attributes/CustomViewAttributesStore";
import { ReactionStore } from "csi/utility/mobxUtility";
import { compact } from "lodash";

class CalculationItem {
	@observable.ref item: ComponentItem = BLANK_COMPONENTITEM;

	@observable.ref calculationFunction: ComponentItem = BLANK_COMPONENTITEM;

	@observable.ref weightedAverageItem?: ComponentItem;

	/** set the calculation / math function for this level */
	@action setCalculationItemCalculationFunction = (calculationFunction: ComponentItem): void => {
		this.calculationFunction = calculationFunction;
	};

	/** set the weighted average property -> available when weighted average is selected for calculation function */
	@action setCalculationItemWeightedAverageItem = (item: ComponentItem): void => {
		this.weightedAverageItem = item;
	};

	get reportItem(): ICustomViewReport | undefined {
		const { calculationFunction, item, requiresSecondKey, weightedAverageItem } = this;
		if (calculationFunction.id && item.id && (!requiresSecondKey || !!weightedAverageItem?.id)) {
			return {
				func: calculationFunction.id,
				key: item.id,
				key2: weightedAverageItem?.id, 
			};
		}
	}

	get requiresSecondKey(): boolean {
		return functionsRequiringSecondKey.includes(this.calculationFunction.id);
	}

	get visible(): string {
		return this.item.id !== "" && this.requiresSecondKey ? "visible" : "hidden";
	}

	constructor() {
		makeObservable(this);
	}
}

export default class CustomViewReportStore extends ReactionStore {
	/** attribute store that contains all the attributes from all levels, using it to extract the correct group of spec*/
	readonly attributeStore: CustomViewAttributesStore;

	/** this record stores the component items that we exclude from showing in the dropdown because the attributes were already selected */
	@observable excludingOptionsList: ObservableMap<string, ComponentItem> = observable.map<string, ComponentItem>(null, { deep: false });

	readonly loc: ILocaleProvider;

	/** stores calculation levels in report pane */
	private _calculationList = observable.array<CalculationItem>([], { deep: false });

	/** option to hide nested reports in hierarchical view */
	@observable hideNestedReports?: boolean;

	/** map of specs admin configures */
	@computed get specMap(): Map<string, Spec> {
		const { attributeSpecsInView } = this.attributeStore;
		const map = new Map<string, Spec>();
		for (const spec of attributeSpecsInView) {
			if (spec.canReport()) {
				map.set(spec.uniqueId, spec);
			}
		}
		return map;
	}

	/** the available math function for reporting */
	get functions(): ComponentItem[] {
		const { loc } = this;
		const componentItems = [];
		const functionNames = reportFunctions;

		functionNames.map((id) => {
			componentItems.push({ id, name: loc.resource("OP_" + id) });
		});

		return componentItems;
	}

	/** returns a list of numeric type attributes except the selected attribute */
	weightedAverageOptions = (id: string): ComponentItem[] => {
		const componentItems = [];
		if (!id) {
			return componentItems;
		}

		const spec = this.specMap.get(id);
		return this.getAttributesAsWeightedReportStoreOptions(id, spec);
	};

	getAttributesAsWeightedReportStoreOptions(id: string, spec: Spec): ComponentItem[] {
		const { mainLevel, convertSpecToComponentItemForCustomView } = this.attributeStore;
		const extendedKeySpec = getSpecForExtendedKeys(spec, mainLevel.specBuilder);

		return organizeComponentItems(extendedKeySpec.filter((keySpec) => keySpec.canReport() && keySpec.uniqueId != id)
			.map((keySpec) => convertSpecToComponentItemForCustomView(keySpec)));
	}

	/** disable add calculation item button if the number of calculation items exceeds the total of selected and unselected report attributes */
	@computed get isDisabled(): boolean {
		return this.options.length + this.excludingOptionsList.size <= this.calculationList.length;
	}

	/** the available options based on selected type */
	@computed get options(): ComponentItem[] {
		const { attributeSpecsInView, convertSpecToComponentItemForCustomView } = this.attributeStore;
		return attributeSpecsInView.filter((spec) => spec.canReport() && !this.excludingOptionsList.has(spec.toString()))
			.map((spec) => convertSpecToComponentItemForCustomView(spec));
	}

	/** the available options based on selected type and the item that was already selected */
	getOptions = (item: ComponentItem): ComponentItem[] => {
		// get specs that can report and are not already selected
		let reportOptions = this.options;
		
		// include the item selected in the dropdown
		if (item.id != "") {
			reportOptions = reportOptions.concat(item);
		}
		return organizeComponentItems(reportOptions);
	};

	/** return the list of calculation levels */
	get calculationList(): CalculationItem[] {
		return this._calculationList;
	}

	/** return the current report settings */
	get currentReportSettings(): ICustomViewReport[] {
		return compact(this._calculationList.map(({ reportItem }) => reportItem));
	}

	/** add a new calculation level to the calculation list */
	@action addCalculationItem = (): void => {
		this._calculationList.push(new CalculationItem());
	};

	/** remove a calculation level from the calculation list */
	@action removeCalculationItem = (index: number): void => {
		this.excludingOptionsList.delete(this._calculationList[index].item.id);
		this._calculationList.splice(index, 1);
	};

	/** set the attribute for the calculation level */
	@action setCalculationItem = (item: ComponentItem, index: number): void => {
		const { excludingOptionsList, calculationList } = this;
		excludingOptionsList.set(item.id, item);
		if (calculationList[index].item.id !== "") {
			excludingOptionsList.delete(calculationList[index].item.id);
		}
		calculationList[index].item = item;

		this._calculationList[index].item = item;
	};

	/** set the value for hideNestedReports */
	@action setHideNestedReports = (e: SyntheticEvent, value: boolean): void => {
		this.hideNestedReports = value;
	};

	/** tracks when attribute in data selection changes (add/remove) and remove any calculation item if the attribute has been removed in data selection */
	removeInvalidCalculationItem = (): void => {
		const reportAttributes = this.attributeStore.attributeSpecsInView.filter((spec) => spec.canReport());
		this.calculationList.forEach((calculationItem, index) => {
			if (!reportAttributes.some((attribute) => attribute.uniqueId === calculationItem.item.id)) {
				this.removeCalculationItem(index);
			} 
		});
	};

	constructor(
		loc: ILocaleProvider,
		reportSettings: ICustomViewReport[],
		attributeStore: CustomViewAttributesStore,
		hideNestedReports?: boolean,
	) {
		super();
		makeObservable(this);
		this.attributeStore = attributeStore;
		this.hideNestedReports = hideNestedReports;
		this.loc = loc;
		const { getSpecForKey } = this.attributeStore.mainLevel.specBuilder;

		reportSettings?.forEach((value, index) => {
			this.addCalculationItem();
			this.setCalculationItem(attributeStore.convertSpecToComponentItemForCustomView(getSpecForKey(value.key)), index);
			this._calculationList[index].setCalculationItemCalculationFunction({ id: value.func, name: loc.resource("OP_" + value.func) });
			if (value.key2) {
				const weightedOption = getSpecForKey(value.key2);
				this._calculationList[index].setCalculationItemWeightedAverageItem(attributeStore.convertSpecToComponentItemForCustomView(weightedOption));
			}
		});

		this.trackReaction(reaction(() => this.attributeStore.attributeSpecsInView, this.removeInvalidCalculationItem));
	}
}

export const CustomViewReportStoreContext = createContext<CustomViewReportStore>(undefined);
export const useCustomViewReportStore = (): CustomViewReportStore => useContext(CustomViewReportStoreContext);